/**
 * Mock-device factory panel (Phase 2) — the super-admin surface for
 * creating simulated data sources and handing out claim codes. Rendered
 * inside the platform console. Mint a source from a blueprint → get a
 * claim code → an end-user adopts it into their org from the Devices page.
 * Purge removes the whole chain (readings + devices + gateway) so mock
 * data never lingers.
 */

import { useEffect, useState } from "react";
import { Boxes, CircleCheck, KeyRound, Loader2, Plus, Trash2 } from "lucide-react";

import {
  factoryApi,
  type FactoryBlueprint,
  type FactorySource,
} from "@/lib/api";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/lib/ui/card";
import { Input } from "@/lib/ui/input";
import { useToast } from "@/lib/ui/toast";
import { cn } from "@/lib/ui/cn";

export function FactoryPanel() {
  const toast = useToast();
  const [blueprints, setBlueprints] = useState<FactoryBlueprint[]>([]);
  const [sources, setSources] = useState<FactorySource[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [name, setName] = useState("");
  const [minting, setMinting] = useState(false);
  const [busy, setBusy] = useState<Set<string>>(new Set());
  const [reminting, setReminting] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);

  const refreshSources = () =>
    factoryApi.sources().then((r) => setSources(r.items)).catch(() => {});

  useEffect(() => {
    let cancelled = false;
    Promise.all([factoryApi.blueprints(), factoryApi.sources()])
      .then(([b, s]) => {
        if (cancelled) return;
        setBlueprints(b.blueprints);
        setSources(s.items);
        if (b.blueprints[0]) setSelected(b.blueprints[0].id);
      })
      .catch(() => toast.push("error", "Couldn't load the factory."))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mint = () => {
    if (!selected) return;
    setMinting(true);
    factoryApi
      .mint(selected, name.trim() || undefined)
      .then((res) => {
        toast.push("ok", `Minted "${res.name}" — claim code ${res.claim_code}`, {
          durationMs: 8000,
        });
        setName("");
        refreshSources();
      })
      .catch(() => toast.push("error", "Couldn't mint the source."))
      .finally(() => setMinting(false));
  };

  const remint = (s: FactorySource) => {
    setReminting((prev) => new Set(prev).add(s.id));
    factoryApi
      .rotateToken(s.id)
      .then((res) => {
        // Codes are hashed at rest (#229) — reveal this one once and copy it
        // so the operator can paste it straight into the device.
        navigator.clipboard?.writeText(res.claim_code).catch(() => {});
        toast.push(
          "ok",
          `New claim code for "${s.name}" — ${res.claim_code} (copied)`,
          { durationMs: 10000 },
        );
        refreshSources();
      })
      .catch(() => toast.push("error", "Couldn't re-mint the claim code."))
      .finally(() =>
        setReminting((prev) => {
          const n = new Set(prev);
          n.delete(s.id);
          return n;
        }),
      );
  };

  const remove = (s: FactorySource) => {
    setBusy((prev) => new Set(prev).add(s.id));
    factoryApi
      .remove(s.id)
      .then(() => {
        toast.push("ok", `Purged "${s.name}"`);
        setSources((prev) => prev.filter((x) => x.id !== s.id));
      })
      .catch(() => toast.push("error", "Couldn't purge the source."))
      .finally(() =>
        setBusy((prev) => {
          const n = new Set(prev);
          n.delete(s.id);
          return n;
        }),
      );
  };

  const sel = blueprints.find((b) => b.id === selected);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-3">
        <div className="flex size-9 items-center justify-center rounded-md bg-brand-muted text-brand">
          <Boxes className="size-5" />
        </div>
        <CardTitle className="flex-1">
          Mock-device factory
          <span className="ml-2 text-sm font-normal text-on-surface-muted">
            {sources.length} source{sources.length === 1 ? "" : "s"}
          </span>
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        {/* Mint */}
        <div className="rounded-md border border-border p-4">
          <div className="mb-3 text-sm font-medium text-on-surface">
            Mint a simulated source
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            {blueprints.map((b) => (
              <button
                key={b.id}
                type="button"
                onClick={() => setSelected(b.id)}
                className={cn(
                  "rounded-md border p-3 text-left transition-colors",
                  selected === b.id
                    ? "border-brand bg-brand-muted/40"
                    : "border-border hover:bg-surface-muted",
                )}
              >
                <div className="font-medium text-on-surface">{b.label}</div>
                <div className="mt-0.5 text-[11px] text-on-surface-muted">
                  {b.description}
                </div>
                <div className="mt-2 flex flex-wrap gap-1">
                  {b.tags.map((t) => (
                    <Badge key={t.name} variant="secondary" className="text-[9px] font-mono">
                      {t.name}
                    </Badge>
                  ))}
                </div>
              </button>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap items-end gap-2">
            <div className="min-w-48 flex-1">
              <label className="text-[11px] text-on-surface-muted">Name (optional)</label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={sel?.label ?? "Source name"}
                className="mt-1"
              />
            </div>
            <Button onClick={mint} disabled={minting || !selected}>
              {minting ? (
                <Loader2 className="size-4 animate-spin" />
              ) : (
                <Plus className="size-4" />
              )}
              Mint source
            </Button>
          </div>
        </div>

        {/* Minted sources */}
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-on-surface-muted">
            <Loader2 className="size-4 animate-spin" /> Loading…
          </div>
        ) : sources.length === 0 ? (
          <div className="py-4 text-sm text-on-surface-muted">
            No simulated sources yet. Mint one above, then claim it from an
            organisation's Devices page using the claim code.
          </div>
        ) : (
          <div className="overflow-hidden rounded-md border border-border">
            {sources.map((s, idx) => (
              <div
                key={s.id}
                className={cn(
                  "flex flex-wrap items-center gap-3 px-3 py-2.5 text-sm",
                  idx > 0 && "border-t border-border",
                )}
              >
                <div className="flex min-w-0 flex-1 flex-col">
                  <span className="flex items-center gap-1.5 font-medium text-on-surface">
                    {s.name}
                    {s.claimed ? (
                      <Badge variant="success" className="text-[9px]">
                        <CircleCheck className="mr-0.5 size-2.5" />
                        claimed{s.owner_org ? ` · ${s.owner_org}` : ""}
                      </Badge>
                    ) : (
                      <Badge variant="warning" className="text-[9px]">
                        unclaimed
                      </Badge>
                    )}
                  </span>
                  <span className="font-mono text-[11px] text-on-surface-muted">
                    {s.slug} · {s.device_count} device
                    {s.device_count === 1 ? "" : "s"} · {s.tag_count} tag
                    {s.tag_count === 1 ? "" : "s"}
                  </span>
                </div>
                {!s.claimed && (
                  <div className="flex items-center gap-2">
                    <Badge
                      variant={s.has_active_code ? "success" : "secondary"}
                      className="text-[9px]"
                    >
                      {s.has_active_code ? "code active" : "no active code"}
                    </Badge>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={reminting.has(s.id)}
                      onClick={() => remint(s)}
                      title="Re-mint a fresh claim code — shown once, then copied"
                    >
                      {reminting.has(s.id) ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        <KeyRound className="size-3.5" />
                      )}
                      Re-mint code
                    </Button>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  disabled={busy.has(s.id)}
                  onClick={() => remove(s)}
                >
                  <Trash2 className="size-3.5" />
                  Purge
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
